import { Container, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import theme from '@components/theme';
import { useRouter } from 'next/router';
import { useTranslation } from '@tools/i18n';
import { ChevronLeft } from '@material-ui/icons';
import MetaTitle from '@components/meta/title';
import MetaDescription from '@components/meta/description';
import Link from "next/link";
import { useContext, useEffect } from 'react';
import { TrackingContext } from '@components/providers/tracking';
import { getServerSidePropsWithAppDataResponse } from '@services/ServerCacheManager';
import statamicEntryFetch from '@connectors/statamic/entry';
import { GetServerSidePropsContext } from 'next';


const useStyles = makeStyles({
    wrapper: {
        padding: '2em 30px',
    },
    mainImage: {
        width: '100%',
        maxWidth: '1140px',
        margin: '1em auto 0',
        display: 'block',
    },
    titleLg: {
        fontFamily: 'Chronicle',
        textAlign: 'center',
        color: '#222',
        position: 'relative',
        zIndex: 5,
        fontSize: '40px',
        margin: '1em auto',
    },
    titleSm: {
        fontFamily: 'didot',
        textAlign: 'center',
        color: '#222',
        position: 'relative',
        zIndex: 5,
        fontSize: '16px',
        lineHeight: '1.5',
        whiteSpace: 'break-spaces',
        maxWidth: '600px',
        margin: '1em auto -1.5em',
    },
    topLine: {
        margin: '4em auto -2.6em',
        textAlign: 'center',
        fontSize: '0.9em',
        display: 'block',
        color: '#575757',
    },
    article: {
        maxWidth: '1140px',
        margin: '1em auto 6em',
        '& a': {
            textDecoration: 'none',
            color: "#222",
        },
        '& img': {
            outline: '1px solid #eee',
            maxWidth: "100%",
        },
        '& h1': {
            fontFamily: 'Chronicle',
            fontWeight: 400,
            margin: '1em 0 -0.5em',
        },
        '& h2': {
            fontFamily: 'Chronicle',
            fontSize: '2em',
            fontWeight: 300,
            margin: '2em 0 -0.2em',
        },
        '& p': {
            color: '#555'
        }
    },
    date: {
        textAlign: 'center',
        margin: '0 auto 0.5em',
        display: 'block',
        color: '#575757',
        fontStyle: 'italic',
    },
    timeAuthor: {
        display: 'inline',
        fontStyle: 'italic',
        fontSize: '0.9em',
        '& time': {
            display: 'inline',
        },
        '& address': {
            display: 'inline',
            marginLeft: '5px'
        }
    },
    cover: {
        transition: 'opacity 0.2 ease',
        '&:hover': {
            opacity: 0.9,
        }
    },
    author: {
        display: 'flex',
        maxWidth: '1140px',
        margin: '0 auto',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '2em',
    },
    portrait: {
        aspectRatio: 1,
        borderRadius: '50%',
        maxWidth: '100%',
        width: '275px',
        display: 'inline-block',
        marginBottom: '1em',
        [theme.breakpoints.down('xs')]: {
            margin: 'auto',
        }

    },
    authorName: {
        marginBottom: '0.5em'
    },
    pathos: {
        width: '100%',
        maxWidth: '500px',
        display: 'inline-block'
    },
    backToList: {
        fontSize: '1em',
        color: '#222',
        marginBottom: '2em'
    }
}, {name: "blog-entry"})

export default function BlogArticle({ title, content }) {
    const classes = useStyles();
    const article = content.data[0];
    const router = useRouter();
    const {actions: trackingActions} = useContext(TrackingContext);
    const {t} = useTranslation(['product-info', 'support']);
    const listUrl = router?.asPath?.match(/^(.*)\//)?.[1];

    useEffect(() => {
        trackingActions.pageImpression(article?.meta_title, 'blog_page');
    }, [])

    return (
        <>
            <MetaTitle title={article?.meta_title || 'Newport Blog'} />
            <MetaDescription description={article?.meta_description} />

            <Container className={classes.wrapper}>
                <Link className={classes.backToList} href={listUrl}>
                    <a className={classes.backToList}>
                        <ChevronLeft style={{fontSize: '14px'}}/>
                        {t('support-back', { ns: 'support' })}
                    </a>
                </Link>

                <img className={classes.mainImage} src={article?.list_image.url} alt={''} />
                <Typography className={classes.topLine} variant={"body1"}>{new Date(article?.date).toLocaleDateString('sv-SE', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                })} · by {article?.author.name}</Typography>
                <Typography className={classes.titleLg} variant={"h2"}>{article?.title}</Typography>
                <div className={classes.article} dangerouslySetInnerHTML={{ __html: article?.content[0].text }}></div>

                <section className={classes.author}>
                    <img className={classes.portrait} src={"/images/niclas.jpg"} alt={"Porträttfoto på Niclas Olsson"} />
                    <div>

                        <Typography className={classes.authorName} variant={"h3"}>{article?.author.name}</Typography>
                        <Typography className={classes.pathos} variant={"body2"}>
                            Niclas är en auktoritet inom heminredning och produktkunskap - en sann visionär som förenar estetik och funktion på ett unikt sätt. Hans förmåga att kombinera olika stilar och skapa harmoni har gjort honom till en eftertraktad rådgivare. Med magnetisk närvaro och gedigen kunskap inspirerar han andra att skapa vackra och funktionella hem. Hans expertis sträcker sig över olika projekt, och hans råd ses som guldstandard inom branschen.
                        </Typography>
                    </div>

                </section>
            </Container>
        </>
    );
}

/**
 *
 * @param context {GetServerSidePropsContext}
 * @returns {Promise<{props: any}>}
 */
export const getServerSideProps = async (context) => {
    const entryResponse = await statamicEntryFetch('newport_blog', context.req);
    return getServerSidePropsWithAppDataResponse(context, entryResponse.status === 'success' ? entryResponse.data : { notFound: true });
};
